import { LOCATION_NAMES } from './location-names.generated';

/** Lookup normalization only; never change the caller's stored name. */
export function normalizeArabicLocation(value: string): string {
  return value
    .normalize('NFKC')
    .replace(/[\u0610-\u061a\u064b-\u065f\u0670\u06d6-\u06ed\u0640]/g, '')
    .replace(/[أإآٱ]/g, 'ا')
    .replace(/[ىی]/g, 'ي')
    .replace(/ک/g, 'ك')
    .trim()
    .replace(/\s+/g, ' ');
}

const COMMON_NAMES: Readonly<Record<string, string>> = {
  دمشق: 'Damascus',
  حلب: 'Aleppo',
  حماة: 'Hamah',
  حماه: 'Hamah',
  حمص: 'Homs',
  اللاذقية: 'Latakia',
  طرطوس: 'Tartus',
  ادلب: 'Idlib',
  الرقة: 'Raqqa',
  'دير الزور': 'Deir ez-Zor',
  الحسكة: 'Al-Hasakah',
  القامشلي: 'Qamishli',
  كوباني: 'Kobani',
  منبج: 'Manbij',
  اعزاز: 'Azaz',
  الباب: 'Al-Bab',
  جرابلس: 'Jarabulus',
  'تل ابيض': 'Tell Abyad',
  'كفر نبل': 'Kafr Nabl',
  كفرنبل: 'Kafr Nabl',
  عفرين: 'Afrin',
  درعا: 'Daraa',
  السويداء: 'As-Suwayda',
};
const PREFIXES: Readonly<Record<string, string>> = {
  كفر: 'Kafr',
  تل: 'Tell',
  عين: 'Ain',
  دير: 'Deir',
  جبل: 'Jabal',
  وادي: 'Wadi',
};
const LETTERS: Readonly<Record<string, string>> = {
  ا: 'a',
  ب: 'b',
  ت: 't',
  ث: 'th',
  ج: 'j',
  ح: 'h',
  خ: 'kh',
  د: 'd',
  ذ: 'dh',
  ر: 'r',
  ز: 'z',
  س: 's',
  ش: 'sh',
  ص: 's',
  ض: 'd',
  ط: 't',
  ظ: 'z',
  ع: 'a',
  غ: 'gh',
  ف: 'f',
  ق: 'q',
  ك: 'k',
  ل: 'l',
  م: 'm',
  ن: 'n',
  ه: 'h',
  ة: 'ah',
  و: 'u',
  ؤ: 'u',
  ي: 'i',
  ئ: 'i',
  ء: "'",
  پ: 'p',
  چ: 'ch',
  ڤ: 'v',
  گ: 'g',
};

/** Readable approximation only: absent short vowels cannot determine official spelling. */
export function transliterateLocation(value: string): string {
  return normalizeArabicLocation(value).replace(
    /[\u0621-\u063a\u0641-\u064a\u0671\u067e\u0686\u06a4\u06af]+/g,
    (word) => {
      if (PREFIXES[word]) return PREFIXES[word];
      const article = word.startsWith('ال') && word.length > 2;
      const stem = article ? word.slice(2) : word;
      const parts = [...stem].map((letter, i) =>
        letter === 'و' && i === 0
          ? 'w'
          : letter === 'ي' && i === 0
            ? 'y'
            : (LETTERS[letter] ?? letter)
      );
      let result = '';
      for (const part of parts) {
        if (/[bcdfghjklmnpqrstvwxyz]$/i.test(result) && /^[bcdfghjklmnpqrstvwxyz]/i.test(part))
          result += 'a';
        result += part;
      }
      return (article ? 'Al-' : '') + result.charAt(0).toUpperCase() + result.slice(1);
    }
  );
}

export class SyrianLocationResolver {
  private readonly cache = new Map<string, string>();
  display(value: string | null | undefined, language: string): string {
    if (value == null) return '';
    if (language.toLowerCase().split('-')[0] !== 'en' || !/[\u0600-\u06ff]/.test(value))
      return value;
    const key = normalizeArabicLocation(value);
    const cached = this.cache.get(key);
    if (cached !== undefined) return cached;
    const result =
      COMMON_NAMES[key] ?? LOCATION_NAMES[key] ?? (transliterateLocation(value) || value);
    if (this.cache.size >= 256) this.cache.delete(this.cache.keys().next().value!);
    this.cache.set(key, result);
    return result;
  }
}
